import React, { Suspense } from "react";
import { storyblokEditable } from "@storyblok/react/rsc";
import { fetchContentType } from "@/lib/storyblok_utils";
import GenericGridClient from "./GenericGridClient";
import Card from "@/components/Card";
import { GridStoryblok } from "@/component-types-sb";
import { containerClassName, itemClassName } from "@/lib/grid-layout";

interface GenericGridProps {
  blok: GridStoryblok;
  data?: {
    version?: "draft" | "published";
  };
}

const GenericGridAsync = async ({
  blok,
  version,
  startIndex,
  requestLimit,
}: {
  blok: GridStoryblok;
  version: "draft" | "published";
  startIndex: number;
  requestLimit: number;
}) => {
  const queryResult = await fetchContentType(
    "",
    version,
    requestLimit,
    undefined,
    undefined,
    blok.type
  );

  return <GenericGridClient blok={blok} initialQueryResult={queryResult} startIndex={startIndex} />;
};

const GenericGrid: React.FC<GenericGridProps> = async ({ blok, data }) => {
  const version = data?.version ?? "published";
  const startIndex = Number(blok.start_index) || 0;
  const configuredCount = Number(blok.count);

  const { total: totalEntries } = await fetchContentType(
    "",
    version,
    1,
    undefined,
    undefined,
    blok.type
  );

  const entryCount = determineEntryCount(configuredCount, totalEntries, startIndex);
  const requestLimit = entryCount + startIndex;

  const isEvent = blok.type === "event";
  const gridContent = <GenericGridAsync blok={blok} version={version} startIndex={startIndex} requestLimit={requestLimit} />;

  return (
    <div {...storyblokEditable(blok)}>
      {isEvent ? (
        gridContent
      ) : (
        <Suspense
          fallback={
            <div className={containerClassName}>
              {Array.from({ length: entryCount }).map((_, index) => {
                const itemIndex = startIndex + index;
                return (
                  <div key={itemIndex} className={itemClassName(false, entryCount)}>
                    <Card
                      isLoading={true}
                      fullWidth={false}
                      blok={{
                        component: "card",
                        _uid: `loading-card-${itemIndex}`,
                      }}
                    />
                  </div>
                );
              })}
            </div>
          }
        >
          {gridContent}
        </Suspense>
      )}
    </div>
  );
};

function determineEntryCount(configuredCount: number, totalEntries: number, startIndex: number) {
  const automaticCount = totalEntries - startIndex;

  if(configuredCount > 0 && configuredCount < automaticCount) 
    return configuredCount;
  
  return automaticCount;
}

export default GenericGrid;
